import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { faqData } from '../assets/data/faqData';
import { teamData } from '../assets/data/teamData';
import ScrollReveal from '../components/ScrollReveal';
import CTASection from '../components/CTASection';
import '../styles/aboutPage.css';

const differentiators = [
  {
    title: 'Business Advisory',
    text: 'Accounting, compliance, tax planning, and financial reporting delivered with precision and clarity.',
  },
  {
    title: 'Technology Delivery',
    text: 'Web, mobile, and custom software solutions built to strengthen operations and digital presence.',
  },
  {
    title: 'One Trusted Partner',
    text: 'Strategy and execution under one roof — so decisions stay aligned from boardroom to build.',
  },
];

const approach = [
  {
    step: '01',
    title: 'Understand',
    text: 'We start by learning your operations, priorities, and constraints — not pushing a generic playbook.',
  },
  {
    step: '02',
    title: 'Plan',
    text: 'Clear scope, realistic timelines, and transparent recommendations you can act on with confidence.',
  },
  {
    step: '03',
    title: 'Deliver',
    text: 'Disciplined execution with ongoing support — so results hold up long after the project ends.',
  },
];

const values = [
  {
    title: 'Precision in Delivery',
    text: 'We execute with discipline, clarity, and attention to the details that matter.',
  },
  {
    title: 'Trusted Partnership',
    text: 'Dependable support you can rely on when decisions carry real business weight.',
  },
  {
    title: 'Commitment to Results',
    text: 'We focus on practical outcomes that strengthen operations and enable growth.',
  },
  {
    title: 'Clear Communication',
    text: 'Transparent guidance keeps teams aligned and decisions straightforward.',
  },
];

const STORY_IMAGE =
  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80';

function AboutPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <>
      <SEO
        title="About Us"
        description="Learn about QASHQ Business & IT Consultants — a professional consulting firm combining business advisory, accounting, and technology expertise to help organizations grow with confidence."
        path="/about"
      />
      <main className="about-page">
        <section className="about-hero">
          <div className="about-hero-bg" aria-hidden="true"></div>
          <div className="container about-hero-inner">
            <ScrollReveal className="about-hero-copy">
              <p className="about-hero-eyebrow">About QASHQ</p>
              <h1 className="about-page-title">
                Business expertise and technology capability — under one roof.
              </h1>
              <p className="about-page-lead">
                QASHQ is a Business &amp; IT consulting firm helping organizations operate with
                financial clarity, regulatory confidence, and the digital tools to grow sustainably.
              </p>
              <div className="about-hero-actions">
                <Link to="/contact" className="btn btn-secondary about-hero-cta">
                  Get in Touch
                </Link>
                <Link to="/services" className="btn btn-outline-light about-hero-cta-secondary">
                  Explore Services
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="about-story">
          <div className="container about-story-grid">
            <ScrollReveal className="about-story-copy">
              <p className="section-eyebrow">Who We Are</p>
              <h2 className="about-story-title">Built for businesses that need more than a vendor.</h2>
              <p className="about-story-text">
                We are not simply an accounting firm or a software agency. QASHQ exists at the
                intersection of business advisory and technology — where financial discipline meets
                practical digital delivery.
              </p>
              <p className="about-story-text">
                From reporting and compliance to custom software and digital transformation, we help
                leadership teams make smarter decisions, reduce operational friction, and build
                foundations that scale.
              </p>
              <blockquote className="about-story-quote">
                <p>
                  &ldquo;Our goal is simple: give every client the clarity to decide and the capability
                  to execute.&rdquo;
                </p>
              </blockquote>
            </ScrollReveal>

            <ScrollReveal as="figure" className="about-story-media" delay={0.08}>
              <img
                src={STORY_IMAGE}
                alt="QASHQ consultants collaborating with a client"
                loading="lazy"
                className="about-story-image"
              />
              <figcaption className="about-story-caption">
                Integrated consulting for modern business operations
              </figcaption>
            </ScrollReveal>
          </div>
        </section>

        <section className="about-diff-section">
          <div className="container">
            <ScrollReveal className="about-section-header about-section-header--center">
              <p className="section-eyebrow">What Sets Us Apart</p>
              <h2 className="section-title">Integrated expertise, delivered with intent.</h2>
            </ScrollReveal>

            <div className="about-diff-grid">
              {differentiators.map((item, index) => (
                <ScrollReveal key={item.title} as="article" className="about-diff-card" delay={index * 0.07}>
                  <span className="about-diff-card__accent" aria-hidden="true"></span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="about-approach-section">
          <div className="container">
            <ScrollReveal className="about-section-header">
              <p className="section-eyebrow">Our Approach</p>
              <h2 className="section-title">How we work with you</h2>
              <p className="about-section-lead">
                A structured, transparent process — from first conversation to long-term partnership.
              </p>
            </ScrollReveal>

            <div className="about-approach-grid">
              {approach.map((item, index) => (
                <ScrollReveal key={item.step} as="article" className="about-approach-card" delay={index * 0.08}>
                  <span className="about-approach-step">{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="about-mv-section">
          <div className="container about-mv-grid">
            <ScrollReveal as="article" className="about-mv-panel">
              <p className="about-mv-label">Our Mission</p>
              <h2>To empower businesses with integrated advisory and technology solutions.</h2>
              <p>
                We help organizations achieve sustainable growth, maintain compliance, and use
                technology as a practical advantage — connecting strategy with execution.
              </p>
            </ScrollReveal>

            <ScrollReveal as="article" className="about-mv-panel about-mv-panel--vision" delay={0.08}>
              <p className="about-mv-label">Our Vision</p>
              <h2>To build smart, adaptable, and resilient business operations.</h2>
              <p>
                We envision a future where every business runs with financial clarity, reliable systems,
                and the digital foundation to adapt as markets evolve.
              </p>
            </ScrollReveal>
          </div>
        </section>

        <section className="about-values-section">
          <div className="container">
            <ScrollReveal className="about-section-header about-section-header--center">
              <p className="section-eyebrow">Our Values</p>
              <h2 className="section-title">Principles that guide every engagement</h2>
            </ScrollReveal>

            <div className="about-values-grid">
              {values.map((value, index) => (
                <ScrollReveal key={value.title} as="article" className="about-value-card" delay={index * 0.06}>
                  <span className="about-value-index">{String(index + 1).padStart(2, '0')}</span>
                  <h3>{value.title}</h3>
                  <p>{value.text}</p>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="about-team-section" id="team">
          <div className="container">
            <ScrollReveal className="about-section-header about-section-header--center">
              <p className="section-eyebrow">Leadership</p>
              <h2 className="section-title">Meet the people behind QASHQ</h2>
              <p className="about-section-lead">
                Experienced co-founders committed to practical results and lasting client relationships.
              </p>
            </ScrollReveal>

            <div className="about-team-grid">
              {teamData.map((member, index) => (
                <ScrollReveal key={member.id} as="article" className="about-team-card" delay={index * 0.08}>
                  <figure className="about-team-photo">
                    <img src={member.image} alt={member.name} loading="lazy" />
                  </figure>
                  <div className="about-team-body">
                    <h3>{member.name}</h3>
                    <p className="about-team-role">{member.title}</p>
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="about-team-link"
                    >
                      LinkedIn
                    </a>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="about-faq-section" id="faq">
          <div className="container about-faq-layout">
            <ScrollReveal className="about-faq-intro">
              <p className="section-eyebrow">FAQ</p>
              <h2 className="section-title">Common questions</h2>
              <p className="about-section-lead">
                Straight answers about how we work, what we offer, and how to get started.
              </p>
              <Link to="/contact" className="about-faq-cta">
                Still have questions? Contact us
              </Link>
            </ScrollReveal>

            <div className="about-faq-list">
              {faqData.map((faq, index) => {
                const isOpen = expandedFaq === faq.id;
                return (
                  <ScrollReveal key={faq.id} as="div" className="about-faq-item" delay={index * 0.04}>
                    <button
                      type="button"
                      onClick={() => toggleFaq(faq.id)}
                      className={`about-faq-question ${isOpen ? 'is-open' : ''}`}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.question}</span>
                      <span className="about-faq-toggle" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    <div className={`about-faq-answer-wrap ${isOpen ? 'is-open' : ''}`}>
                      <div className="about-faq-answer">{faq.answer}</div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        <CTASection
          eyebrow="Ready to get started"
          title="Connect with us to explore tailored business and IT solutions."
          primaryLabel="Get in Touch"
        />
      </main>
    </>
  );
}

export default AboutPage;
